import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmService } from '@core/services/confirm.service';
import { FarmDeviceService } from '@core/services/farm-device/farm-device.service';
import { AddDeviceDialogComponent } from '@shared/component/my-account/devices/add-device-dialog/add-device-dialog.component';
import { FarmDeviceStatus } from '@shared/model/farm-device-status.enum';
import { FarmDevice, FarmDeviceFarm } from '@shared/model/farm-device.model';
import { EMPTY, Subject, takeUntil } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

interface FarmDeviceGroup {
	farm: FarmDeviceFarm;
	devices: FarmDevice[];
}

@Component({
	selector: 'sf-my-account-devices-list',
	templateUrl: './my-account-devices-list.component.html',
	styleUrls: ['./my-account-devices-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyAccountDevicesListComponent implements OnChanges, OnDestroy {
	@Input() devices: FarmDevice[] | null = null;
	@Output() changed: EventEmitter<void> = new EventEmitter<void>();

	public FarmDeviceStatus: typeof FarmDeviceStatus = FarmDeviceStatus;

	private readonly destroy: Subject<void> = new Subject();
	private readonly disconnectingIds = new Set<string>();
	private readonly deletingIds = new Set<string>();
	private readonly reissuingIds = new Set<string>();
	private readonly previewingIds = new Set<string>();

	// Built once per `devices` input change rather than as a template getter: a getter re-runs
	// on every change detection cycle (e.g. mouse movement over a tooltip trigger) and returns
	// new array/object instances each time, which makes the untracked *ngFor below tear down and
	// rebuild every row on each cycle - breaking hover-timed tooltips and in-flight clicks.
	public groups: FarmDeviceGroup[] = [];

	constructor(
		private readonly modal: MatDialog,
		private readonly confirm: ConfirmService,
		private readonly farmDeviceService: FarmDeviceService,
		private readonly cdr: ChangeDetectorRef
	) {}

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes.devices) {
			return;
		}
		const currentDevices = this.devices ?? [];
		this.groups = this.buildGroups(currentDevices);

		// A row's pending state is cleared once the refreshed list actually confirms the
		// outcome (device gone / status changed), not when the HTTP call merely completes -
		// otherwise the spinner disappears a full refresh-cycle before the row itself updates.
		const idsById = new Map(currentDevices.map((device) => [device.id, device]));

		for (const id of Array.from(this.deletingIds)) {
			if (!idsById.has(id)) {
				this.deletingIds.delete(id);
			}
		}
		for (const id of Array.from(this.disconnectingIds)) {
			if (idsById.get(id)?.status !== FarmDeviceStatus.CONNECTED) {
				this.disconnectingIds.delete(id);
			}
		}
		// Reissuing doesn't have one fixed target status to check for (it can land on PENDING or
		// elsewhere depending on the dialog outcome), so any refreshed list landing after the
		// dialog closes counts as confirmation, whatever status it reports.
		this.reissuingIds.clear();
	}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	public deviceNumberLabel(device: FarmDevice): string {
		return String(device.deviceNumber).padStart(3, '0');
	}

	public trackByFarmId(_index: number, group: FarmDeviceGroup): string {
		return group.farm.id;
	}

	public trackByDeviceId(_index: number, device: FarmDevice): string {
		return device.id;
	}

	private buildGroups(devices: FarmDevice[]): FarmDeviceGroup[] {
		const groups = new Map<string, FarmDeviceGroup>();

		for (const device of devices) {
			if (!groups.has(device.farm.id)) {
				groups.set(device.farm.id, { farm: device.farm, devices: [] });
			}
			groups.get(device.farm.id).devices.push(device);
		}

		return [...groups.values()];
	}

	public isDisconnecting(device: FarmDevice): boolean {
		return this.disconnectingIds.has(device.id);
	}

	public isDeleting(device: FarmDevice): boolean {
		return this.deletingIds.has(device.id);
	}

	public isReissuing(device: FarmDevice): boolean {
		return this.reissuingIds.has(device.id);
	}

	public isPreviewing(device: FarmDevice): boolean {
		return this.previewingIds.has(device.id);
	}

	public isDevicePending(device: FarmDevice): boolean {
		return (
			this.isDisconnecting(device) || this.isDeleting(device) || this.isReissuing(device) || this.isPreviewing(device)
		);
	}

	public onDisconnectClick(device: FarmDevice): void {
		if (this.isDevicePending(device)) {
			return;
		}
		const keyPrefix = 'sf.shared_component.my_account.devices_section.disconnect_dialog.';
		this.confirm
			.openModal({
				title: `${keyPrefix}title`,
				description: `${keyPrefix}description`,
				confirmButtonText: `${keyPrefix}confirm`,
				cancelButtonText: 'sf.common.cancel',
			})
			.afterClosed()
			.pipe(
				filter((confirmed: boolean) => confirmed),
				switchMap(() => {
					this.disconnectingIds.add(device.id);
					this.cdr.markForCheck();
					return this.farmDeviceService.disconnect(device.id).pipe(
						catchError(() => {
							// handled globally by the unknown-error interceptor; on error there is no
							// list refresh coming, so clear the pending state right here instead of
							// waiting for ngOnChanges, which only fires once a refreshed list arrives
							this.disconnectingIds.delete(device.id);
							this.cdr.markForCheck();
							return EMPTY;
						})
					);
				}),
				takeUntil(this.destroy)
			)
			.subscribe(() => this.changed.emit());
	}

	public onDeleteClick(device: FarmDevice): void {
		if (this.isDevicePending(device)) {
			return;
		}
		const keyPrefix = 'sf.shared_component.my_account.devices_section.delete_dialog.';
		this.confirm
			.openModal({
				title: `${keyPrefix}title`,
				description: `${keyPrefix}description`,
				confirmButtonText: `${keyPrefix}confirm`,
				cancelButtonText: 'sf.common.cancel',
			})
			.afterClosed()
			.pipe(
				filter((confirmed: boolean) => confirmed),
				switchMap(() => {
					this.deletingIds.add(device.id);
					this.cdr.markForCheck();
					return this.farmDeviceService.delete(device.id).pipe(
						catchError(() => {
							// handled globally by the unknown-error interceptor; on error there is no
							// list refresh coming, so clear the pending state right here instead of
							// waiting for ngOnChanges, which only fires once a refreshed list arrives
							this.deletingIds.delete(device.id);
							this.cdr.markForCheck();
							return EMPTY;
						})
					);
				}),
				takeUntil(this.destroy)
			)
			.subscribe(() => this.changed.emit());
	}

	public onGenerateNewCodeClick(device: FarmDevice): void {
		if (this.isDevicePending(device)) {
			return;
		}
		this.reissuingIds.add(device.id);
		this.cdr.markForCheck();
		this.modal
			.open(AddDeviceDialogComponent, {
				width: '480px',
				height: 'auto',
				autoFocus: 'auto',
				hasBackdrop: true,
				disableClose: true,
				data: { mode: 'reissue', device },
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe(() => this.changed.emit());
	}

	public onPreviewCodeClick(device: FarmDevice): void {
		if (this.isDevicePending(device)) {
			return;
		}
		this.previewingIds.add(device.id);
		this.cdr.markForCheck();
		this.modal
			.open(AddDeviceDialogComponent, {
				width: '480px',
				height: 'auto',
				autoFocus: 'auto',
				hasBackdrop: true,
				disableClose: true,
				data: { mode: 'preview', device },
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {
				// Preview doesn't mutate the device's server-side state, so unlike
				// reissue the pending flag can clear immediately on close instead of
				// waiting for a list refresh to confirm anything.
				this.previewingIds.delete(device.id);
				this.cdr.markForCheck();
			});
	}
}
